import { useEffect, useMemo, useState } from 'react';
import type { EnumDef, FieldDef, TypeDef } from '../types/model';
import { collectFieldNamesInChain, collectFieldNamesInSubtree, formatFieldTypeLabel, getDescendantIds, normalizeName } from '../utils/modelUtils';
import { FieldRow } from './FieldRow';

type TypeEditorProps = {
  typeDef: TypeDef | null;
  types: TypeDef[];
  enums: EnumDef[];
  onUpdateType: (typeId: string, updater: (type: TypeDef) => TypeDef) => void;
  onUpdateField: (typeId: string, fieldId: string, updater: (field: FieldDef) => FieldDef) => void;
  onAddField: (typeId: string) => void;
  onDeleteField: (typeId: string, fieldId: string) => void;
  onDeleteType: (typeId: string) => void;
  onSelectType: (typeId: string) => void;
  focusFieldId: string | null;
  onClearFieldFocus: () => void;
  onJsonErrorChange: (fieldId: string, hasError: boolean) => void;
  clearedDefaultFields: Record<string, boolean>;
  onDefaultUpdated: (fieldId: string) => void;
  enforceIndexedInheritance: boolean;
};

export const TypeEditor = ({
  typeDef,
  types,
  enums,
  onUpdateType,
  onUpdateField,
  onAddField,
  onDeleteField,
  onDeleteType,
  onSelectType,
  focusFieldId,
  onClearFieldFocus,
  onJsonErrorChange,
  clearedDefaultFields,
  onDefaultUpdated,
  enforceIndexedInheritance,
}: TypeEditorProps) => {
  const [emptyNameFields, setEmptyNameFields] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!typeDef) {
      setEmptyNameFields({});
      return;
    }
    const activeIds = new Set(typeDef.fields.map((field) => field.id));
    setEmptyNameFields((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((key) => {
        if (activeIds.has(key) && prev[key]) {
          next[key] = true;
        }
      });
      return next;
    });
  }, [typeDef]);

  const hasEmptyName = useMemo(() => Object.keys(emptyNameFields).length > 0, [emptyNameFields]);
  const handleNameEmptyChange = (fieldId: string, isEmpty: boolean) => {
    setEmptyNameFields((prev) => {
      const next = { ...prev };
      if (isEmpty) {
        next[fieldId] = true;
      } else {
        delete next[fieldId];
      }
      return next;
    });
  };

  if (!typeDef) {
    return null;
  }

  const typesById = new Map(types.map((type) => [type.id, type]));
  const descendantIds = new Set(getDescendantIds(types, typeDef.id));
  const subtreeFieldNames = collectFieldNamesInSubtree(types, typeDef.id);
  const inheritedGroups: Array<{ owner: TypeDef; fields: FieldDef[] }> = [];
  const inheritedMap = new Map<string, { owner: TypeDef; fields: FieldDef[] }>();
  const seen = new Set<string>();
  let currentParentId = typeDef.parentTypeId;
  while (currentParentId) {
    if (seen.has(currentParentId)) {
      break;
    }
    seen.add(currentParentId);
    const parentType = typesById.get(currentParentId);
    if (!parentType) {
      break;
    }
    let group = inheritedMap.get(parentType.id);
    if (!group) {
      group = { owner: parentType, fields: [] };
      inheritedMap.set(parentType.id, group);
      inheritedGroups.push(group);
    }
    parentType.fields.forEach((field) => group!.fields.push(field));
    currentParentId = parentType.parentTypeId;
  }

  const parentOptions = types
    .filter((type) => type.id !== typeDef.id)
    .map((type) => {
      const reasons: string[] = [];
      if (descendantIds.has(type.id)) {
        reasons.push('descendant');
      }
      const chainNames = collectFieldNamesInChain(typesById, type.id);
      const conflict = Array.from(subtreeFieldNames).some((name) => chainNames.has(name));
      if (conflict) {
        reasons.push('field conflict');
      }
      if (enforceIndexedInheritance && typeDef.isIndexed && !type.isIndexed) {
        reasons.push('non-indexed');
      }
      return {
        id: type.id,
        name: type.name,
        valid: reasons.length === 0,
        reason: reasons.join(', '),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));

  const isNameTaken = (name: string, fieldId: string) => {
    const key = normalizeName(name);
    if (!key) {
      return false;
    }
    return typeDef.fields.some((field) => field.id !== fieldId && normalizeName(field.name) === key);
  };

  return (
    <section className="panel editor">
      <div className="panel-header">
        <div className="panel-title">Type Editor</div>
        <div className="header-actions">
          <button type="button" className="button ghost" onClick={() => onDeleteType(typeDef.id)}>
            Delete Type
          </button>
        </div>
      </div>
      <div className="editor-grid">
        <div className="field-column">
          <label className="label">Name</label>
          <input
            className="input"
            type="text"
            value={typeDef.name}
            onChange={(event) => onUpdateType(typeDef.id, (type) => ({ ...type, name: event.target.value }))}
            placeholder="e.g. Order"
          />
        </div>
        <div className="field-column">
          <label className="label">Parent type</label>
          <select
            className="input"
            value={typeDef.parentTypeId ?? ''}
            onChange={(event) =>
              onUpdateType(typeDef.id, (type) => ({
                ...type,
                parentTypeId: event.target.value ? event.target.value : null,
              }))
            }
          >
            <option value="">None</option>
            {parentOptions.map((option) => (
              <option key={option.id} value={option.id} disabled={!option.valid}>
                {option.name || 'Unnamed type'}
                {!option.valid && option.reason ? ` (${option.reason})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field-column compact">
          <label className="label">Indexed</label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={typeDef.isIndexed}
              onChange={(event) => onUpdateType(typeDef.id, (type) => ({ ...type, isIndexed: event.target.checked }))}
            />
            <span>{typeDef.isIndexed ? 'Indexed' : 'Not indexed'}</span>
          </label>
        </div>
      </div>
      <div className="editor-grid">
        <div className="field-column full">
          <label className="label">Description</label>
          <textarea
            className="input"
            rows={3}
            value={typeDef.description}
            onChange={(event) => onUpdateType(typeDef.id, (type) => ({ ...type, description: event.target.value }))}
            placeholder="Describe the intent of this type."
          />
        </div>
      </div>
      <div className="fields-header">
        <h3>Fields</h3>
        <button type="button" className="button primary" onClick={() => onAddField(typeDef.id)} disabled={hasEmptyName}>
          Add field
        </button>
      </div>
      {hasEmptyName && <div className="muted">Name all fields before adding a new one.</div>}
      {inheritedGroups.length > 0 && (
        <div className="inherit-section">
          <div className="fields-header">
            <h3>Inherited fields</h3>
            <div className="muted">Read-only</div>
          </div>
          {inheritedGroups.map((group) => (
            <div key={group.owner.id} className="inherit-group">
              <div className="inherit-title">From {group.owner.name || 'Unnamed type'}</div>
              <div className="inherit-list">
                {group.fields.map((field) => {
                  const label = formatFieldTypeLabel(field.type, types, enums);
                  const typeLabel = field.isArray ? `${label}[]` : label;
                  return (
                    <div key={field.id} className="inherit-row">
                      <span className="inherit-name">{field.name || 'Unnamed field'}</span>
                      <span className="inherit-type">{typeLabel}</span>
                      {field.isRequired && <span className="meta-pill">Required</span>}
                      {field.isArray && <span className="meta-pill">Array</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {typeDef.fields.length === 0 && <div className="muted">No fields yet. Add one to start defining shape.</div>}
      <div className="field-list">
        {typeDef.fields.map((field) => (
          <FieldRow
            key={field.id}
            field={field}
            typeOptions={types}
            enumOptions={enums}
            onUpdate={(patch) =>
              onUpdateField(typeDef.id, field.id, (current) => ({
                ...current,
                ...patch,
              }))
            }
            onUpdateConstraints={(patch) =>
              onUpdateField(typeDef.id, field.id, (current) => ({
                ...current,
                constraints: { ...current.constraints, ...patch },
              }))
            }
            onDelete={() => onDeleteField(typeDef.id, field.id)}
            onSelectType={onSelectType}
            onJsonErrorChange={onJsonErrorChange}
            isFocused={focusFieldId === field.id}
            onFocusHandled={onClearFieldFocus}
            isDefaultCleared={Boolean(clearedDefaultFields[field.id])}
            onDefaultUpdated={() => onDefaultUpdated(field.id)}
            onNameEmptyChange={handleNameEmptyChange}
            isNameTaken={(name) => isNameTaken(name, field.id)}
          />
        ))}
      </div>
    </section>
  );
};
